/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';
import { FloatingPortal, autoUpdate, flip, offset, size as floatingSize, useFloating } from '@floating-ui/react';

import type { SearchBoxAutoCompleteProps, SearchBoxProps } from '../../types';
import Icon from '../Icon/Icon';
import Spinner from '../Spinner/Spinner';
import './SearchBox.less';

// ---------------------------------------------------------------------------
// Simple SearchBox
// ---------------------------------------------------------------------------
const SearchBox = React.forwardRef<HTMLInputElement, SearchBoxProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      onSearch,
      placeholder = 'Search…',
      disabled = false,
      size = 'medium',
      id,
      name,
      'aria-label': ariaLabel,
      className,
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState(defaultValue ?? '');
    const currentValue = isControlled ? value : internalValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setInternalValue(e.target.value);
      onChange?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSearch?.(currentValue ?? '');
      }
    };

    return (
      <span
        className={cx('ds-search-box', `ds-search-box--${size}`, { 'ds-search-box--disabled': disabled }, className)}>
        <span className="ds-search-box__icon" aria-hidden="true">
          <Icon name="actionable/search" size="small" />
        </span>
        <input
          ref={ref}
          id={id}
          name={name}
          type="search"
          aria-label={ariaLabel ?? placeholder}
          className="ds-search-box__input"
          value={currentValue}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      </span>
    );
  }
);

SearchBox.displayName = 'SearchBox';

// ---------------------------------------------------------------------------
// AutoComplete
// ---------------------------------------------------------------------------
const AutoComplete = React.forwardRef<HTMLInputElement, SearchBoxAutoCompleteProps>(
  (
    {
      open,
      onOpenChange,
      loading = false,
      noDataMessage = 'No results',
      children,
      onSearch,
      className,
      size = 'medium',
      disabled = false,
      ...rest
    },
    ref
  ) => {
    const isControlled = open !== undefined;
    const [internalOpen, setInternalOpen] = React.useState(false);
    const isOpen = isControlled ? !!open : internalOpen;
    const wrapperRef = React.useRef<HTMLSpanElement | null>(null);

    const openDropdown = React.useCallback(() => {
      if (!isControlled) setInternalOpen(true);
      onOpenChange?.(true);
    }, [isControlled, onOpenChange]);

    const closeDropdown = React.useCallback(() => {
      if (!isControlled) setInternalOpen(false);
      onOpenChange?.(false);
    }, [isControlled, onOpenChange]);

    const { refs, floatingStyles } = useFloating({
      placement: 'bottom-start',
      strategy: 'fixed',
      middleware: [
        offset(4),
        flip(),
        floatingSize({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, { width: `${rects.reference.width}px` });
          },
        }),
      ],
      whileElementsMounted: autoUpdate,
    });

    // Outside click — capture phase so React Flow canvas stopPropagation doesn't block it
    React.useEffect(() => {
      if (!isOpen) return;
      const handler = (e: MouseEvent | TouchEvent) => {
        const el = wrapperRef.current;
        if (!el || el.contains(e.target as Node)) return;
        if (refs.floating.current && refs.floating.current.contains(e.target as Node)) return;
        closeDropdown();
      };
      document.addEventListener('mousedown', handler, { capture: true });
      document.addEventListener('touchstart', handler, { capture: true });
      return () => {
        document.removeEventListener('mousedown', handler, { capture: true });
        document.removeEventListener('touchstart', handler, { capture: true });
      };
    }, [isOpen, closeDropdown, refs.floating]);

    // Escape to close
    React.useEffect(() => {
      if (!isOpen) return;
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') closeDropdown();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, closeDropdown]);

    const setWrapperRef = React.useCallback(
      (node: HTMLSpanElement | null) => {
        wrapperRef.current = node;
        refs.setReference(node);
      },
      [refs]
    );

    const hasChildren = React.Children.count(children) > 0;

    return (
      <>
        <span
          ref={setWrapperRef}
          className={cx('ds-search-box', `ds-search-box--${size}`, { 'ds-search-box--disabled': disabled }, className)}>
          <span className="ds-search-box__icon" aria-hidden="true">
            <Icon name="actionable/search" size="small" />
          </span>
          <input
            ref={ref}
            type="search"
            className="ds-search-box__input"
            disabled={disabled}
            autoComplete="off"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            onFocus={openDropdown}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSearch?.((e.target as HTMLInputElement).value);
              }
            }}
            {...rest}
          />
        </span>

        {isOpen && (
          <FloatingPortal>
            <div ref={refs.setFloating} className="ds-search-box__dropdown" style={floatingStyles} role="listbox">
              {loading ? (
                <div className="ds-search-box__dropdown-loader">
                  <Spinner />
                </div>
              ) : hasChildren ? (
                children
              ) : (
                <div className="ds-search-box__dropdown-empty">{noDataMessage}</div>
              )}
            </div>
          </FloatingPortal>
        )}
      </>
    );
  }
);

AutoComplete.displayName = 'SearchBox.AutoComplete';

// Compound component
type SearchBoxComponent = typeof SearchBox & { AutoComplete: typeof AutoComplete };
(SearchBox as SearchBoxComponent).AutoComplete = AutoComplete;

export default SearchBox as SearchBoxComponent;
